import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { getIsOnAuth2Path } from '@/core/auth/utils/getIsOnAuth2Path';
import { paths } from '@/core/router/paths';
import { useAuthUsername } from '@/core/store/auth/authSelectors';
import { useAuthStore } from '@/core/store/auth/authStore';

import { useRefreshTokenMutation } from '../_graphql/mutation/RefreshToken.hook';

import { useClearClientAuthData } from './useClearClientAuthData';

export const useRefreshToken = () => {
	const navigate = useNavigate();
	const username = useAuthUsername();
	const setIdToken = useAuthStore(state => state.setIdToken);
	const { pathname } = useLocation();
	const [refreshing, setRefreshing] = useState(true);
	const [isOnline, setIsOnline] = useState(true);
	const { clearClientAuthData } = useClearClientAuthData();

	const [refreshToken] = useRefreshTokenMutation({
		variables: { username: username }
	});

	useEffect(() => {
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);

		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	const handleTokenRefresh = useCallback(async () => {
		const isOutsideAuthPaths = !getIsOnAuth2Path(pathname);

		if (isOnline && isOutsideAuthPaths) {
			try {
				const res = await refreshToken();
				const responseBody = res?.data?.signIn;

				if (responseBody && responseBody.__typename === 'SignInResult') {
					const { idToken } = responseBody;

					setIdToken(idToken);
					window.dispatchEvent(new StorageEvent('storage'));
				} else {
					// Note(Filip): Do not use the signOut mutation here as it will invalidate the refresh token stored in the cookie.
					// Instead, we clear the client auth store and redirect to the login page.
					clearClientAuthData();
					navigate(paths.login);
				}
			} catch {
				// Note(Filip): Do not use the signOut mutation here as it will invalidate the refresh token stored in the cookie.
				// Instead, we clear the client auth store and redirect to the login page.
				clearClientAuthData();
				navigate(paths.login);
			} finally {
				setRefreshing(false);
			}
		} else {
			setRefreshing(false);
		}
	}, [
		clearClientAuthData,
		isOnline,
		navigate,
		pathname,
		refreshToken,
		setIdToken
	]);

	return { handleTokenRefresh, isRefreshing: refreshing };
};
